import { zodResolver } from '@hookform/resolvers/zod';
import { useAtom } from 'jotai';
import type { ReactElement } from 'react';
import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { SingleValue } from 'react-select';
import { styled } from 'goober';
import { z } from 'zod';

import type { CreateUserApiKeyPayload } from '@/api/v4/user-api-key.api';
import { modalState } from '@/state/modal.state';
import { Flexbox } from '@/ui/flexbox/flexbox';
import { Input } from '@/ui/input/input';
import { Stack } from '@/ui/line/line';
import { Modal } from '@/ui/modal/modal';
import type { SelectOptionValue } from '@/ui/select/select';
import { Select } from '@/ui/select/select';

import { useCreateUserApiKey } from '../../settings/api-and-integrations/user-api-keys/use-create-user-api-key.mutation';

const USER_API_KEY_EXPIRATIONS = [
  'none',
  '30d',
  '60d',
  '90d',
  '1y',
] as const satisfies readonly NonNullable<
  CreateUserApiKeyPayload['expiration']
>[];

type UserApiKeyExpiration = (typeof USER_API_KEY_EXPIRATIONS)[number];

const createTokenSchema = z.object({
  expiration: z.enum(USER_API_KEY_EXPIRATIONS),
  name: z.string().min(1).max(200),
});

type CreateTokenFormValues = z.infer<typeof createTokenSchema>;

export const CreateAccessTokenModal = (): ReactElement | null => {
  const { t } = useTranslation('settings', { keyPrefix: 'accessTokensTab' });
  const { t: tDefault } = useTranslation('default');
  const [modal, setModal] = useAtom(modalState);
  const { createToken, isLoading: isCreateLoading } = useCreateUserApiKey();

  const {
    control,
    formState: { errors, isSubmitting, isValid },
    handleSubmit,
    register,
    reset,
  } = useForm<CreateTokenFormValues>({
    defaultValues: {
      expiration: 'none',
      name: '',
    },
    mode: 'onChange',
    resolver: zodResolver(createTokenSchema),
  });

  const expirationOptions =
    useMemo((): SelectOptionValue<UserApiKeyExpiration>[] => {
      const labelByValue: Record<UserApiKeyExpiration, string> = {
        '1y': t`expirationOptions.1year`,
        '30d': t`expirationOptions.30days`,
        '60d': t`expirationOptions.60days`,
        '90d': t`expirationOptions.90days`,
        none: t`expirationOptions.noExpiration`,
      };

      return USER_API_KEY_EXPIRATIONS.map(value => ({
        label: labelByValue[value],
        value,
      }));
    }, [t]);

  if (modal?.state !== 'createAccessToken') {
    return null;
  }

  const close = (): void => {
    reset({
      expiration: 'none',
      name: '',
    });
    setModal(null);
  };

  const onSubmit = async (values: CreateTokenFormValues): Promise<void> => {
    try {
      const payload: CreateUserApiKeyPayload = {
        expiration: values.expiration,
        name: values.name.trim(),
      };

      const result = await createToken(payload);
      reset({
        expiration: 'none',
        name: '',
      });
      setModal({
        rawKey: result.rawKey,
        state: 'accessTokenCreated',
      });
    } catch {
      // Errors are reported by useCreateUserApiKey mutation onError.
    }
  };

  return (
    <Modal
      disabled={!isValid || isSubmitting}
      header={t`createModal.title`}
      loading={isCreateLoading || isSubmitting}
      mainAction={{
        label: t`form.generate`,
        onClick: handleSubmit(onSubmit),
      }}
      minWidth="520px"
      onClose={close}
      secondaryAction={{
        label: tDefault`cancel`,
        onClick: close,
      }}
      variant="info"
    >
      <CreateTokenModalBody direction="column" fullWidth>
        <Stack gap="16px" margin="16px 0">
          <NameFieldColumn direction="column" gap="6px">
            <FieldLabel htmlFor="access-token-name">{t`form.name`}</FieldLabel>
            <Input
              error={errors.name?.message}
              id="access-token-name"
              placeholder={t`form.namePlaceholder`}
              width="100%"
              {...register('name')}
            />
          </NameFieldColumn>
          <FieldStack gap="6px" minWidth="200px" width="240px">
            <FieldLabel htmlFor="access-token-expiration">
              {t`form.expiration`}
            </FieldLabel>
            <Controller
              control={control}
              name="expiration"
              render={({ field: { onBlur, onChange, value } }) => (
                <Select<UserApiKeyExpiration>
                  inputId="access-token-expiration"
                  onBlur={onBlur}
                  onChange={(
                    newValue: SingleValue<
                      SelectOptionValue<UserApiKeyExpiration>
                    >,
                  ) => {
                    if (newValue) {
                      onChange(newValue.value);
                    }
                  }}
                  options={expirationOptions}
                  value={
                    expirationOptions.find(option => option.value === value) ??
                    null
                  }
                />
              )}
            />
          </FieldStack>
        </Stack>
      </CreateTokenModalBody>
    </Modal>
  );
};

const CreateTokenModalBody = styled(Flexbox)`
  margin: 18px 0 12px;
`;

const NameFieldColumn = styled(Flexbox)`
  flex: 1 1 200px;
  min-width: 200px;
`;

const FieldStack = styled(Stack)<{ minWidth?: string; width?: string }>`
  min-width: ${({ minWidth }) => minWidth ?? '0'};
  width: ${({ width }) => width ?? 'auto'};
`;

const FieldLabel = styled('label')`
  color: ${({ theme }) => theme.colors.gray.c10};
  font-family: ${({ theme }) => theme.fonts.wallop};
  font-size: 12px;
  font-weight: 500;
`;
